// Shared BOM helpers so the form and card stay in lockstep.
export const isTray = (name) => /tray/i.test(name || "");
export const isPackSplit = (name) => /tray|shrink/i.test(name || "");
export const isAutoQty = (name) => /preform|cap|label/i.test(name || "");
export const isPalletLevel = (name) => /pallet|stretch/i.test(name || "");
export const isPerBottle = (name) => /water|h2o/i.test(name || "");
export const packQty = (pack) => (String(pack) === "24" ? 24 : 12);

// Flag-aware variants (fall back to the name rules for un-migrated components).
export const basisOf = (c) => c?.consumption_basis || (isPalletLevel(c?.name) ? "per_pallet" : isPackSplit(c?.name) ? "per_case" : "per_bottle");
export const dimOf = (c) => {
  const m = c?.inventory_mode_effective || c?.inventory_mode || "";
  const lenRoll = (c?.types || []).some((t) => (t?.roll_content_type === "ft" || t?.roll_content_type === "m") && Number(t?.content_per_stock_unit) > 0);
  if (m === "label") return "count";
  if (m === "film") return lenRoll ? "length" : "weight";
  if (lenRoll) return "length";
  return c?.dimension || (isPerBottle(c?.name) ? "volume" : "count");
};
export const isPackSplitC = (c) => (c && typeof c.pack_size_variant === "boolean" ? c.pack_size_variant : isPackSplit(c?.name));
export const isAutoC = (c) => (c?.dimension ? basisOf(c) === "per_bottle" && dimOf(c) === "count" : isAutoQty(c?.name));
export const isVolC = (c) => dimOf(c) === "volume";
export const isPalletLevelC = (c) => (c?.consumption_basis ? basisOf(c) === "per_pallet" : isPalletLevel(c?.name));

// Line-aware basis: a Product BOM line can override the component's default consumption basis.
export const basisOfLine = (l, c) => (l && l.basis) || basisOf(c);
export const isAutoLine = (l, c) => basisOfLine(l, c) === "per_bottle" && dimOf(c) === "count";
export const isPalletLine = (l, c) => basisOfLine(l, c) === "per_pallet";
export const isTruckLine = (l, c) => basisOfLine(l, c) === "per_truckload";

// Group BOM lines by consumption level: bottle -> case -> pallet -> truckload (stable within groups).
export const BASIS_RANK = { per_bottle: 0, per_case: 1, per_pallet: 2, per_truckload: 3 };
export const groupLinesByBasis = (lines, compById = {}) =>
  [...lines].sort((a, b) => {
    const rank = (l) => (l.component_id ? (BASIS_RANK[basisOfLine(l, compById[l.component_id])] ?? 0) : 99);
    return rank(a) - rank(b);
  });

// --- Dimension-aware quantity units (Count / Volume / Weight / Length) ---
export const BASE_UNIT = { volume: "mL", weight: "g", length: "m", count: "" };
export const UNIT_SYSTEMS = {
  volume: { metric: ["mL", "L", "kL"], imperial: ["fl oz", "gal"] },
  weight: { metric: ["g", "kg"], imperial: ["oz", "lb"] },
  length: { metric: ["mm", "cm", "m"], imperial: ["in", "ft"] },
};
export const UNIT_TO_BASE = {
  mL: 1, L: 1000, kL: 1e6, "fl oz": 29.5735, gal: 3785.41,
  g: 1, kg: 1000, oz: 28.3495, lb: 453.592,
  mm: 0.001, cm: 0.01, m: 1, in: 0.0254, ft: 0.3048,
};
export const defaultUnit = (dim) => BASE_UNIT[dim] || "";
export const unitFactor = (u) => UNIT_TO_BASE[u] || 1;
export const systemOfUnit = (dim, u) => (UNIT_SYSTEMS[dim]?.imperial.includes(u) ? "imperial" : "metric");
export const unitsForDim = (dim, system) => UNIT_SYSTEMS[dim]?.[system] || [];
// Entered quantity converted to the dimension's base unit (mL / g / m / each).
const FILM_W_MM = { mm: 1, cm: 10, in: 25.4 };
const FILM_T_UM = { um: 1, mil: 25.4 };
// grams per metre of film = width(mm) × thickness(µm) × density(g/cm³) / 1000
export const filmGramsPerMeter = (t) => {
  const w = Number(t?.film_width) || 0;
  const th = Number(t?.film_thickness) || 0;
  const d = Number(t?.material_density) || 0;
  if (w <= 0 || th <= 0 || d <= 0) return null;
  return w * (FILM_W_MM[t?.film_width_unit || "mm"] ?? 1) * th * (FILM_T_UM[t?.film_thickness_unit || "um"] ?? 1) * d / 1000;
};

export const qtyInBase = (l, comp, cfgId) => {
  const dim = dimOf(comp);
  const cq = cfgId != null && l?.config_quantities ? l.config_quantities[cfgId] : null;
  const val = cq != null && cq !== "" ? cq : l?.quantity;
  const blank = val == null || val === "";
  const raw = blank ? (dim === "count" ? 1 : 0) : (Number(val) || 0);
  if (dim === "count") return raw;
  return raw * unitFactor(l?.qty_unit || defaultUnit(dim));
};
export const fmtNum = (n) => (n == null || isNaN(n) ? "—" : (Math.round(n * 100) / 100).toLocaleString());
// Format a base-unit amount back into the line's chosen unit for display.
// Big lengths auto-promote (mm/cm → m at ≥10 m, in → ft at ≥10 ft) and big volumes
// (mL → L at ≥1 L, → kL at ≥10 kL, fl oz → gal at ≥10 gal) so pallet/truckload BOMs stay readable.
export const fmtInUnit = (dim, base, unit) => {
  if (dim === "count") return fmtNum(base);
  let u = unit || defaultUnit(dim);
  if (dim === "length") {
    if ((u === "mm" || u === "cm") && base >= 10) u = "m";
    else if (u === "in" && base >= 3.048) u = "ft";
  } else if (dim === "volume") {
    if ((u === "mL" || u === "L") && base >= 1e7) u = "kL";
    else if (u === "mL" && base >= 1000) u = "L";
    else if (u === "fl oz" && base >= 37854.1) u = "gal";
  }
  return `${fmtNum(base / unitFactor(u))} ${u}`;
};
